import { useCallback, useEffect, useState } from "react";

/**
 * How many columns an auto-fill CSS grid lays out right now, kept up to date as it
 * resizes, so a page of cards can fill whole rows. Pass the returned ref to the grid.
 * `fallback` holds until the grid is measured (and in tests, where nothing is laid out).
 */
export function useGridColumns(fallback: number): [(el: HTMLElement | null) => void, number] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [cols, setCols] = useState(fallback);
  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);

  useEffect(() => {
    if (!el) return;
    const measure = () => {
      // Laid out, the computed value lists one track per column: "250px 250px 250px".
      const tracks = getComputedStyle(el).gridTemplateColumns.trim();
      if (/^[\d.]+px( [\d.]+px)*$/.test(tracks)) setCols(tracks.split(" ").length);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);

  return [ref, cols];
}

/** Cards per page that fill whole rows: a few rows of the grid's columns. */
export function pageSizeFor(cols: number): number {
  return cols * (cols >= 3 ? 4 : 6);
}
